import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Space, Typography, message } from "antd";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { updateTransactionPlan } from "../../api/transactionPlanApi";
import { useTranslation } from "../../i18n/LanguageProvider";
import TransactionPlanFormFields from "./TransactionPlanFormFields";
import { buildTransactionPlanPayload } from "./transactionPlanUi";

const { Text } = Typography;

/**
 * Admin edit of one row. Uses the same field set as manual entry, pre-filled from the record.
 */
function TransactionPlanEditModal({ open, record, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !record) return;

        setError("");
        form.setFieldsValue({
            sender: record.sender || "",
            beneficiary: record.beneficiary || "",
            beneficiaryBank: record.beneficiaryBank || "",
            currency: record.currency || undefined,
            amount: record.amount === null || record.amount === undefined
                ? undefined
                : Number(record.amount),
            executionDate: record.executionDate ? dayjs(record.executionDate) : undefined,
        });
    }, [open, record, form]);

    const handleSubmit = async () => {
        setError("");

        try {
            const values = await form.validateFields();

            setSaving(true);

            const saved = await updateTransactionPlan(
                record.id,
                buildTransactionPlanPayload(values)
            );

            message.success(t("edit.saved"));
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            if (err?.errorFields) return;

            const text = err?.message || t("edit.saveFailed");
            setError(text);
            message.error(text);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    <span>{t("edit.modalTitle")}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={1000}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>{t("action.close")}</Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSubmit}
                    >
                        {t("action.save")}
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Text type="secondary">{t("edit.hint")}</Text>

                {error && (
                    <Alert type="error" showIcon message={t("edit.saveError")} description={error} />
                )}

                <Form form={form} layout="vertical" requiredMark autoComplete="off">
                    <TransactionPlanFormFields t={t} />
                </Form>
            </Space>
        </Modal>
    );
}

export default TransactionPlanEditModal;
